import React, { useMemo, useRef } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import Button from "@mui/material/Button";
import { Trans } from "react-i18next";
import useTemplateInstance from "../../../hooks/useTemplateInstance";
import {
  ACTIONS,
  getPartRefIds,
  partRefIdIsFirstPartRef,
  partRefIdIsLastPartRef,
} from "../../../../../Providers/TemplateInstanceProvider";
import IconButton from "@mui/material/IconButton";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateAfterIcon from "@mui/icons-material/NavigateNext";
import makeStyles from "@mui/styles/makeStyles";
import useComponentSize from "@rehooks/component-size";
import { getBoxSize } from "../../../../../utils";
import DrawTemplate from "../../../../../components/Template/DrawTemplate";
import useDialogBackButton from "@/react/hooks/useDialogBackButton";

const useStyles = makeStyles({
  root: {
    display: "flex",
    padding: 0,
  },
  templateContainer: {
    position: "absolute",
    "& .template-inner-container": {
      boxShadow: "0px 3px 6px #00000029",
    },
    "&.no-shadow .template-inner-container": {
      boxShadow: "none",
    },
  },
  previousNextContainer: {
    display: "flex",
    alignItems: "center",
    zIndex: 10,
    flexBasis: "48px",
  },
  templateOuterContainer: {
    flexGrow: 1,
    display: "flex",
    position: "relative",
  },
  stretch: {
    flexGrow: 1,
    position: "relative",
    display: "flex",
  },
});

export default ({ onClose }) => {
  const [state, dispatch] = useTemplateInstance();
  const classes = useStyles();

  // Hardware/browser back closes the preview instead of navigating away.
  useDialogBackButton(true, onClose);

  const previousPage = () => {
    dispatch({
      type: ACTIONS.PREVIOUS_PARTREF,
    });
  };

  const nextPage = () => {
    dispatch({
      type: ACTIONS.NEXT_PARTREF,
    });
  };

  const isFirstPage = partRefIdIsFirstPartRef(
    state.currentPartRefId,
    state.template,
  );
  const isLastPage = partRefIdIsLastPartRef(
    state.currentPartRefId,
    state.template,
  );

  const isMultiPage = getPartRefIds(state.template).length > 1;

  return (
    <Dialog open={true} fullScreen={true}>
      <DialogTitle>
        <Trans>Preview</Trans>
      </DialogTitle>
      <DialogContent classes={{ root: classes.root }}>
        <div className={classes.stretch}>
          {isMultiPage && (
            <div className={classes.previousNextContainer}>
              {!isFirstPage && (
                <IconButton onClick={previousPage}>
                  <NavigateBeforeIcon />
                </IconButton>
              )}
            </div>
          )}
          <TemplateContainer />
          {isMultiPage && (
            <div className={classes.previousNextContainer}>
              {!isLastPage && (
                <IconButton onClick={nextPage}>
                  <NavigateAfterIcon />
                </IconButton>
              )}
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          <Trans>Close</Trans>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const TemplateContainer = ({ templateInstance, pageNum, year }) => {
  const [state, dispatch] = useTemplateInstance();

  const onSelectResource = (resource) => {
    dispatch({
      type: ACTIONS.SET_CURRENT_RESOURCE,
      id: resource.fullId,
    });
  };

  const ref = useRef();
  // `current` points to the mounted img element.
  const size = useComponentSize(ref);

  const [cSize, style] = useMemo(() => {
    const horizontalPadding = 5;
    const verticalPadding = 10;
    const cSize = getBoxSize("fit-in", state.template.dimensions, {
      width: size.width - horizontalPadding * 2,
      height: size.height - verticalPadding * 2,
    });
    const style = {
      width: cSize.width + "px",
      height: cSize.height + "px",
      left: cSize.x + horizontalPadding + "px",
      top: cSize.y + verticalPadding + "px",
    };
    return [cSize, style];
  }, [size]);

  const classes = useStyles();
  return (
    <div ref={ref} className={classes.templateOuterContainer}>
      <div
        className={`${classes.templateContainer} ${
          state.template.separateFromBackground ? "" : "no-shadow"
        }`}
        style={style}
      >
        <DrawTemplate
          containerSize={cSize}
          templateInstance={state}
          pageNum={pageNum}
          year={year}
          handleUserResourceClick={onSelectResource}
        />
      </div>
    </div>
  );
};
